import React from 'react';
import moment from 'moment';
import { uniq, get, round } from 'lodash';
import Icon from '../Icon/Icon';
import store from '../../store';
import { Amount } from 'src_common/common/amount/Amount';
import { formatAmountWithCurrency } from 'src_common/common/amount/website-money/currency';
import { getAccumulatorPrice, betTypesNotForAccumulatorPrice } from './accumulator.ts';

const isCarouselUniverse = store.getState().user.toJS().data.universe === 'carousel';

const columnsPartOne = [
    {
        name: 'placedAt',
        label: 'Time',
        minWidth: 0,
        value: (row) => {
            return (
                <span className='time-customer'>
                    <span className='time-xx'>{row.placedAt && moment(row.placedAt).format('DD/MM/YYYY HH:mm')}</span>
                    <br />
                    {row.account && row.account.name}
                </span>
            );
        },
    },
    {
        name: 'stakeFactor',
        label: 'SF',
        minWidth: 0,
    },
    {
        name: 'inPlay',
        label: 'In-play',
        minWidth: 0,
        value: (row) => (row.legs.reduce((value, next) => value && next.inPlay, true) ? 'Y' : 'N'),
    },
    {
        name: 'sport',
        label: 'Sport',
        minWidth: 777,
        value: (row) =>
            uniq(row.legs.map((leg) => leg.sport && (leg.sport.name || leg.sport.id)))
                .filter((x) => x)
                .join(', ') || '-',
    },
    {
        name: 'competition',
        label: 'Competition',
        minWidth: 0,
        value: (row) =>
            uniq(row.legs.map((leg) => leg.competition && leg.competition.name))
                .filter((x) => x)
                .join(', ') || '-',
    },
    {
        name: 'event',
        label: 'Event',
        minWidth: 777,
        value: (row) => {
            const title = uniq(row.legs.map((leg) => leg.event.name)).join(', ');
            return (
                <span title={title} className='truncated'>
                    {title}
                </span>
            );
        },
    },
    {
        name: 'market',
        label: 'Market',
        minWidth: 0,
        value: (row) => {
            if (row.type === 'RAB' || row.type === 'BB') {
                return row.legs[0].market.name;
            } else {
                return (
                    uniq(
                        row.legs.map((leg) => leg.market && (leg.market.name || leg.market.type)).filter((x) => x)
                    ).join(', ') || '-'
                );
            }
        },
    },
    {
        name: 'selection-data',
        label: 'Selection',
        minWidth: 0,
        value: (row) => {
            if (row.type === 'RAB' || row.type === 'BB') {
                return row.legs[0].selection.name;
            } else {
                return row.legs.map((leg) => leg.selection && leg.selection.name).join(', ');
            }
        },
    },
    {
        name: 'price',
        label: 'Price',
        minWidth: 0,
        value: (row) => {
            const leg = row.legs[0];

            if (leg.priceType === 'sp') {
                return leg.spPrice ? leg.spPrice.f : '';
            } else {
                return leg.price ? leg.price.f : '';
            }
        },
    },
    {
        name: 'decimalPrice',
        label: 'Decimal Price',
        minWidth: 0,
        value: (row) => {
            const leg = row.legs[0];

            if (leg.priceType === 'sp') {
                return leg.spPrice ? round(leg.spPrice.d, 2) : '';
            } else {
                return leg.price ? round(leg.price.d, 2) : '';
            }
        },
    },
    {
        name: 'accumulator',
        label: 'Accumulator price',
        minWidth: 0,
        value: getAccumulatorPrice,
    },
    {
        name: 'priceType',
        label: 'Price type',
        minWidth: 777,
        value: (row) => row.legs[0].priceType.toUpperCase(),
    },
];

const columnsPartTwo = [
    {
        name: 'ew',
        label: 'EW',
        minWidth: 0,
        value: (row) => row.eachWay !== undefined && (row.eachWay ? 'Y' : 'N'),
    },
    {
        name: 'ew-terms',
        label: 'EW Terms',
        minWidth: 0,
        value: (row) => {
            const ewTerms = get(row, 'legs.0.eachWayTerms');

            const reduction = get(ewTerms, 'reduction');

            if (ewTerms) {
                if (typeof reduction === 'string') {
                    return `${reduction} for ${get(ewTerms, 'places')} place`;
                } else {
                    const den = reduction && reduction.den ? reduction.den : '';
                    const num = reduction && reduction.num ? reduction.num : '';
                    return `${num}/${den} for ${get(ewTerms, 'places')} place`;
                }
            }

            return '';
        },
    },
    {
        name: 'type',
        label: 'Bet type',
        minWidth: 0,
    },
    {
        name: 'stakePerLine',
        label: 'Stake per line',
        minWidth: 0,
        value: (row) => {
            const isFreeBet = (get(row, 'transaction.tags.freebetCredits') || []).length > 0;

            return (
                <span>
                    {isFreeBet && row.stakePerLine ? <Icon name='gift' /> : undefined}
                    {row.stakePerLine !== undefined &&
                        formatAmountWithCurrency(
                            row.currency,
                            $appState.configState.precision.newFromAnything(row.stakePerLine)
                        )}
                </span>
            );
        },
    },
    {
        name: 'totalStake',
        label: 'Total stake',
        minWidth: 777,
        value: (row) => {
            const isFreeBet = (get(row, 'transaction.tags.freebetCredits') || []).length > 0;

            return (
                <span>
                    {isFreeBet && row.totalStake ? <Icon name='gift' /> : undefined}
                    {row.totalStake !== undefined &&
                        formatAmountWithCurrency(
                            row.currency,
                            $appState.configState.precision.newFromAnything(row.totalStake)
                        )}
                </span>
            );
        },
    },
    isCarouselUniverse && {
        name: 'tax',
        label: 'Tax',
        minWidth: 0,
        value: (row) => {
            return row.tax
                ? formatAmountWithCurrency(row.currency, $appState.configState.precision.newFromAnything(row.tax))
                : '--';
        },
    },
    {
        name: 'results',
        label: 'Results',
        minWidth: 0,
        value: (row) => {
            const leg = row.legs && row.legs[0];
            return row.cashOut ? 'Cashed Out' : (leg.result && leg.result.type) || '--';
        },
    },
    {
        name: 'payout',
        label: 'Payout',
        minWidth: 0,
        value: (row) => row.payout !== undefined && (row.payout === null ? '--' : row.payout),
    },
    {
        name: 'maxLineStake',
        label: 'Max line stake',
        minWidth: 0,
        value: (row) => row.maxBet !== undefined && row.maxBet,
    },
    {
        name: 'stakePerLine/maxAllowedBet',
        label: 'Stake per line/Max allowed bet',
        minWidth: 0,
        value: (row) =>
            !!row.maxBet &&
            $appState.configState.precision
                .newFromAnything(row.stakePerLine)
                .div(parseFloat(row.maxBet))
                .multiply(100)
                .toFixed(2) + '%',
    },
    {
        name: 'channel',
        label: 'Channel',
        minWidth: 0,
    },
    {
        name: 'status',
        label: 'Status',
        minWidth: 0,
    },
    {
        name: 'affiliate',
        label: 'Affiliate',
        minWidth: 0,
        value: (row) => row.affiliate,
    },
    {
        name: 'ip',
        label: 'Ip Address',
        minWidth: 0,
    },
];

const spColumn = [
    {
        name: 'startingPrice',
        label: 'Starting price',
        minWidth: 0,
        value: (row) => {
            if (row.legs.length > 0) {
                const leg = row.legs[0];
                if (leg === undefined) {
                    return '';
                }
                if (row.status === 'settled') {
                    if (
                        leg.sport.id === 'horseracing' ||
                        leg.sport.id === 'greyhoundracing' ||
                        leg.sport.id === 'internationalhorseracing'
                    ) {
                        if (leg.spPrice === null) {
                            return '';
                        } else {
                            return round(leg.spPrice.d, 2);
                        }
                    }
                } else {
                    return '';
                }
            }
        },
    },
];

export const COLUMNS = columnsPartOne.concat(columnsPartTwo);
export const COLUMNS_WITH_SP = columnsPartOne.concat(spColumn, columnsPartTwo);
